import React, { useState } from "react";
import { ColumnDef } from "@tanstack/react-table";
import {
  ArrowDownLeft,
  ArrowLeftRight,
  ArrowUpDown,
  ArrowUpRight,
  ChevronDown,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  ColumnFiltersState,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Button,
  Label,
  Badge,
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui";

import { formatDate, formatValue } from "@/utils";
import { FinanceCashFlows } from "@/types";

import { Pagination } from "../../pagination";
import { cn } from "@/lib";

type FinanceCashFlowDataTableProps = {
  response: FinanceCashFlows;
  per_page: number;
  handlePerPageChange: (newPerPage: any) => void;
};

/**
 * The four cash-flow actions, each with a semantic token, a soft chip and a
 * dot: status is shown with colour **and** shape, never colour alone
 * (design-system.md §2). Mirrors `entries/entry-status.ts`.
 */
const ACTION_TONE: Record<string, { chip: string; dot: string; icon: string }> = {
  paid: {
    chip: "bg-app-success/10 text-app-fg-strong hover:bg-app-success/20",
    dot: "bg-app-success",
    icon: "text-app-success",
  },
  received: {
    chip: "bg-brand-soft text-app-fg-strong hover:bg-brand/20",
    dot: "bg-brand",
    icon: "text-brand",
  },
  to_receive: {
    chip: "bg-app-info/10 text-app-fg-strong hover:bg-app-info/20",
    dot: "bg-app-info",
    icon: "text-app-info",
  },
  to_pay: {
    chip: "bg-app-danger/10 text-app-fg-strong hover:bg-app-danger/20",
    dot: "bg-app-danger",
    icon: "text-app-danger",
  },
};

const NEUTRAL_TONE = {
  chip: "bg-app-surface-muted text-app-fg-strong hover:bg-app-border-subtle",
  dot: "bg-app-fg-subtle",
  icon: "text-app-fg-muted",
};

export type ColunsFinanceCashFlowProps = {
  id: string;
  action: string;
  value: string;
  description: string;
  created_at: string;
};

export function FinanceCashFlowDataTable({
  response: { items, data: paginationData },
  per_page,
  handlePerPageChange,
}: FinanceCashFlowDataTableProps) {
  const { t } = useTranslation();

  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
    []
  );
  const [columnVisibility, setColumnVisibility] =
    React.useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = useState({});

  function getData() {
    const data = items.map((item) => ({
      id: item.finance_entry_id,
      action: item.action,
      value: item.value,
      description: item.description,
      maturity: item.entry.date,
      created_at: item.created_at,
    }));

    return data;
  }

  const columns: ColumnDef<ColunsFinanceCashFlowProps>[] = [
    {
      accessorKey: "action",
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            className="px-0"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("LABEL.actions")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        );
      },
      cell: ({ row }) => {
        const action = String(row.getValue("action"))
          .toLowerCase()
          .replace(/ /g, "_");
        const tone = ACTION_TONE[action] ?? NEUTRAL_TONE;
        return (
          <div className="flex items-center gap-2">
            {action === "paid" && (
              <ArrowDownLeft className={cn("w-4 h-4", tone.icon)} aria-hidden="true" />
            )}
            {action === "received" && (
              <ArrowDownLeft className={cn("w-4 h-4", tone.icon)} aria-hidden="true" />
            )}
            {action === "to_receive" && (
              <ArrowLeftRight className={cn("w-4 h-4", tone.icon)} aria-hidden="true" />
            )}
            {action === "to_pay" && (
              <ArrowUpRight className={cn("w-4 h-4", tone.icon)} aria-hidden="true" />
            )}
            <Badge data-status={action} className={cn("gap-1.5", tone.chip)}>
              <span
                aria-hidden="true"
                className={cn("size-1.5 rounded-full", tone.dot)}
              />
              {t(`LABEL.${action.toLowerCase().replace(/ /g, "_")}`)}
            </Badge>
          </div>
        );
      },
    },
    {
      accessorKey: "value",
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            className="px-0"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("LABEL.value")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        );
      },
      cell: ({ row }) => (
        <div className="capitalize">
          {formatValue(Number(row.getValue("value")))}
        </div>
      ),
    },
    {
      accessorKey: "maturity",
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            className="px-0"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("LABEL.maturity")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        );
      },
      cell: ({ row }) => (
        <div className="capitalize">{formatDate(row.getValue("maturity"))}</div>
      ),
    },
    {
      accessorKey: "created_at",
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            className="px-0"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("LABEL.operation date")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        );
      },
      cell: ({ row }) => {
        return (
          <div className="capitalize">
            {formatDate(row.getValue("created_at"))}
          </div>
        );
      },
    },
  ];


  const dataMemo = React.useMemo(() => getData(), [items]);

  const columnsMemo = React.useMemo(() => columns, []);

  const table = useReactTable({
    data: dataMemo,
    columns: columnsMemo,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
    },
    initialState: {
      pagination: {
        pageSize: per_page,
        pageIndex: 0,
      },
    },
  });
  return (
    <div className="w-full">
      <div className="flex items-center py-4">
        <div className="flex items-center gap-x-3 max-w-sm">
          <Label>{t("LABEL.show")}</Label>
          <select
            onChange={(e) => handlePerPageChange(Number(e.target.value))}
            value={per_page}
            className="py-1 px-2 text-sm rounded-lg border border-app-border bg-app-surface-muted outline-none focus:border-brand"
          >
            <option value="10">10</option>
            <option value="25">25</option>
            <option value="50">50</option>
            <option value="100">100</option>
          </select>
          <Label>itens</Label>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              className="ml-auto px-5 py-2 rounded-lg border-[1px] border-app-border text-base bg-app-surface-muted outline-none focus:border-brand"
            >
              {t("LABEL.columns")} <ChevronDown className="ml-2 h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {table
              .getAllColumns()
              .filter((column) => column.getCanHide())
              .map((column) => {
                return (
                  <DropdownMenuCheckboxItem
                    key={column.id}
                    checked={column.getIsVisible()}
                    // onCheckedChange={(value) => column.toggleVisibility(!!value)}
                    onCheckedChange={(value) => {
                      setColumnVisibility((prev) => ({
                        ...prev,
                        [column.id]: !!value,
                      }));
                    }}
                  >
                    {t(`LABEL.${column.id.toLowerCase()}`)}
                  </DropdownMenuCheckboxItem>
                );
              })}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && "selected"}
                >
                  {row.getVisibleCells().map((cell) => {
                    const isExcludedColumn = cell.column.id === "select";
                    return (
                      <>
                        {!isExcludedColumn ? (
                          <TableCell key={cell.id}>
                            <div className="cursor-pointer">
                              {flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext()
                              )}
                            </div>
                          </TableCell>
                        ) : (
                          <TableCell key={cell.id}>
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </TableCell>
                        )}
                      </>
                    );
                  })}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  {t("No results")}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <Pagination data={paginationData} />
    </div>
  );
}
